import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { EdnaWrapper } from '@providers/index';
import { ORANGE_LEGACY_TEXT_STYLES as TEXT_STYLES } from '@junglescout/styles-library';

import Button, { ButtonType } from '@components/Button';
import Popover from '@components/Popover';
import Separator from '@components/Separator';
import TextInput from '@components/TextInput';
import ToggleButton from '@components/ToggleButton';
import Tooltip from '@components/Tooltip';

import { t } from '../../services/i18n';
import { AddToGroupDropdownProps } from './types';

const ContentWrapper = styled.div`
  padding: 12px;
`;
const Title = styled.div`
  ${TEXT_STYLES.H5Light};
  margin-bottom: 8px;
`;
const List = styled.div`
  border: 1px solid ${(props) => props.theme.colors.grey100};
  max-height: 162px;
  overflow: auto;
`;
const ListItems = styled.div<{ disabled?: boolean }>`
  ${TEXT_STYLES.BodyBlack};
  padding: 8px 12px;
  ${(props) =>
    props.disabled
      ? `
      opacity: 0.5;
      cursor: not-allowed;
      `
      : `
      &:hover {
        background-color: ${props.theme.colors.grey100};
        cursor: pointer;
      }
  `}
`;
const SeparatorContainer = styled.div`
  ${TEXT_STYLES.H5Light};
  color: ${(props) => props.theme.colors.grey500};
  display: flex;
  gap: 8px;
  margin: 8px 0;
`;
const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
`;
const ShareWithTeamWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 12px 0 4px 0;
`;
const ToggleButtonWrapper = styled.div`
  display: flex;
  align-items: center;
`;
const Label = styled.label`
  ${TEXT_STYLES.H5Light};
`;

const CreateGroupSection = ({
  handleCreate,
  showShareWithTeam,
  label,
  createLabel,
}: Pick<
  AddToGroupDropdownProps,
  'handleCreate' | 'showShareWithTeam' | 'label' | 'createLabel'
>) => {
  const [groupName, setGroupName] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);

  return (
    <>
      <TextInput
        label={label}
        placeholder={t('common:Enter', 'Enter')}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          setGroupName(e.target.value)
        }
      />
      {showShareWithTeam && (
        <ShareWithTeamWrapper>
          <Label>{t('common:ShareWithTeam', 'Share with my team')}</Label>
          <ToggleButtonWrapper>
            <ToggleButton
              checked={!isPrivate}
              handleToggleClick={(toggleValue) => setIsPrivate(!toggleValue)}
              margin="0 0 0 8px"
            />
          </ToggleButtonWrapper>
        </ShareWithTeamWrapper>
      )}
      <Footer>
        <Button
          btnType={ButtonType.PRIMARY}
          disabled={!groupName?.trim()}
          margin="8px 0 0 0"
          onClick={() => handleCreate(groupName, isPrivate)}
        >
          {createLabel}
        </Button>
      </Footer>
    </>
  );
};

const AddToGroupContent = ({
  handleCreate,
  handleSelect,
  label,
  createLabel,
  items,
  showShareWithTeam,
  title,
  isCreateButtonDisabled,
  disabledCreateTooltipText,
}: Omit<AddToGroupDropdownProps, 'children' | 'onOpenChange'>) => {
  const [showCreateGroup, setShowCreateGroup] = useState(false);

  return (
    <ContentWrapper onClick={(e) => e.stopPropagation()}>
      <Title>{title}</Title>

      {items?.length ? (
        <>
          <List>
            {items?.map((item) => (
              <ListItems
                key={item.id}
                disabled={item.disabled}
                onClick={(e: any) =>
                  !item.disabled ? handleSelect(item, e) : null
                }
              >
                {item.name}
              </ListItems>
            ))}
          </List>
          <SeparatorContainer>
            <span>{t('common:Or', 'or')}</span>
            <Separator margin="8px 0 8px 0" />
          </SeparatorContainer>
        </>
      ) : null}

      {showCreateGroup ? (
        <CreateGroupSection
          handleCreate={handleCreate}
          label={label}
          createLabel={createLabel}
          showShareWithTeam={showShareWithTeam}
        />
      ) : (
        <Tooltip
          content={disabledCreateTooltipText}
          openOnActivation
          disabled={!isCreateButtonDisabled}
        >
          <div>
            <Button
              btnType={ButtonType.TERTIARY}
              iconName="PLUS_SQUARE"
              margin="0"
              width="100%"
              onClick={() => setShowCreateGroup(!showCreateGroup)}
              disabled={isCreateButtonDisabled}
            >
              {createLabel}
            </Button>
          </div>
        </Tooltip>
      )}
    </ContentWrapper>
  );
};

const AddToGroupDropdown: FC<AddToGroupDropdownProps> = ({
  children,
  className,
  handleCreate,
  handleSelect,
  label = t('common:GroupName', 'Group Name'),
  createLabel = t('common:CreateNewGroup', 'Create new group'),
  isOpen,
  items,
  onOpenChange,
  showShareWithTeam = true,
  title,
  isCreateButtonDisabled = false,
  disabledCreateTooltipText,
}: AddToGroupDropdownProps) => {
  return (
    <EdnaWrapper>
      <Popover
        className={className}
        width="300px"
        open={isOpen}
        onOpenChange={onOpenChange}
        content={
          <AddToGroupContent
            handleCreate={handleCreate}
            handleSelect={handleSelect}
            label={label}
            createLabel={createLabel}
            items={items}
            showShareWithTeam={showShareWithTeam}
            title={title}
            isCreateButtonDisabled={isCreateButtonDisabled}
            disabledCreateTooltipText={disabledCreateTooltipText}
          />
        }
      >
        {children}
      </Popover>
    </EdnaWrapper>
  );
};

export default AddToGroupDropdown;
